import { Icon } from '@timelyapp/react';
import ClearableButton from 'components/buttons/ClearableButton';
import ClearFilterButton from 'components/buttons/ClearFilterButton';
import LinkedWorkSyncFilterDropdown from 'components/dropdowns/LinkedWorkSyncFilterDropdown';
import SearchingProjectFilterDropdown from 'components/dropdowns/SearchingProjectFilterDropdown';
import TabbedUserFilterDropdown from 'components/dropdowns/TabbedUserFilterDropdown';
import TagFilterDropdown from 'components/dropdowns/TagFilterDropdown';
import FeatureGatedButton from 'components/FeatureGatedButton';
import { TimesheetScopes } from 'components/timesheets/types';
import type { TeamFilterDropdownOption } from 'factories/teams';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from 'redux/create';
import type { HourState } from 'redux/modules/hourStates.schema';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import type { ReportProjectIds, ReportQuery } from 'redux/modules/superreports.types';
import { getHasTicIntegrationsSupport } from 'redux/selectors/accounts';
import HourStateDropdown from 'routes/supercalendar/components/calendar/HourStateDropdown';
import type { Account } from 'types/account';
import type { Client } from 'types/client';
import type { EnrichedProject, FilterGroupValues } from 'types/project';
import type { Tag, TagFilterMode } from 'types/tag';
import type { User } from 'types/user';
import type { DeepPartial } from 'types/utility';
import {
  getShowHourStateDropdownFilter,
  getShowUpgradeModalForHourStateDropdownFilter,
} from '../../redux/modules/hourStates';
import styles from './ReportQueryFilters.module.scss';

interface Props {
  className?: string;
  query: ReportQuery;
  onSetQuery: (query: DeepPartial<ReportQuery>) => void;
  onClearQuery: () => void;
  hasModifiedFilters: boolean;

  /**
   * If true, the billing options will be displayed as a dropdown instead of a toggle button.
   * @default false
   */
  billingOptionsDropdown?: boolean;

  /**
   * Controls whether the filters should be scoped to a user or a project.
   * @default TimesheetScopes.All
   */
  scope?: TimesheetScopes;

  /**
   * If true, the "Clear filters" button will not be shown when filters have changed.
   * @default false
   */
  noClearButton?: boolean;

  // The below are pure guesswork, change as needed
  account: Account;
  teams: Array<TeamFilterDropdownOption>;
  users: Array<User>;
  clients: Array<Client>;
  projects: Array<EnrichedProject>;
  hourStates: Record<number, HourState>;
  tags: Array<Tag>;
  onDisabledFeatureClick?: () => void;
  children?: React.ReactNode;
}

const ReportQueryFilters: React.FC<Props> = props => {
  const {
    query,
    onSetQuery,
    account,
    users,
    teams,
    projects,
    clients,
    hasModifiedFilters,
    scope = TimesheetScopes.All,
    noClearButton,
  } = props;
  const { filters } = query;
  const [isHourStateDropdownOpen, setIsHourStateDropdownOpen] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const showUpgradeModalForHourStateDropdownFilter = useSelector(
    getShowUpgradeModalForHourStateDropdownFilter
  );
  const showHourStateDropdown = useSelector(getShowHourStateDropdownFilter);
  const showLinkedWorkSyncFilter = useSelector(getHasTicIntegrationsSupport);

  const showUpgradeModal = useCallback(() => {
    dispatch(showSubscriptionNoticeModal('featureHourStatesRequiresUpgrade'));
  }, [dispatch]);

  const handleSetQuery = useCallback(
    (query: DeepPartial<ReportQuery>) => {
      onSetQuery(query);
    },
    [onSetQuery]
  );

  const handleCloseHourStateDropdown = useCallback(() => {
    setIsHourStateDropdownOpen(false);
  }, []);

  const handleTagFilterChange = useCallback(
    (label_ids: Array<number>) => {
      handleSetQuery({ filters: { label_ids } });
    },
    [handleSetQuery]
  );

  const handleProjectFilterChange = useCallback(
    (project_ids: ReportProjectIds, filter_group?: FilterGroupValues) => {
      handleSetQuery({ filters: { project_ids, filter_group } });
    },
    [handleSetQuery]
  );

  const handleProjectFilterDropdownSelectAll = useCallback(
    (project_ids: ReportProjectIds) => {
      handleSetQuery({ filters: { project_ids, filter_group: undefined } });
    },
    [handleSetQuery]
  );

  const handleHourStateFilterDropdownChange = useCallback(
    (stateIds: number[] | null) =>
      handleSetQuery({ filters: { state_ids: Array.isArray(stateIds) ? stateIds : [] } }),
    [handleSetQuery]
  );

  const handleUserTeamDropdownFilterChange = useCallback(
    (type: 'users' | 'teams', ids: Array<number>) => {
      switch (type) {
        case 'users':
        default:
          handleSetQuery({ filters: { user_ids: ids, team_ids: [] } });
          break;
        case 'teams':
          handleSetQuery({ filters: { user_ids: [], team_ids: ids } });
          break;
      }
    },
    [handleSetQuery]
  );

  const handleTagFilterModeChange = useCallback(
    (mode: TagFilterMode) => handleSetQuery({ filters: { label_filter: mode } }),
    [handleSetQuery]
  );

  const handleLinkedWorkSyncFilterChange = useCallback(
    (externalLinkFilters?: string[], syncStatusFilters?: string[]) => {
      handleSetQuery({
        filters: {
          external_link_filter: externalLinkFilters?.join(','),
          sync_status_filter: syncStatusFilters?.join(','),
        },
      });
    },
    [handleSetQuery]
  );

  return (
    <div className={styles.container}>
      {showUpgradeModalForHourStateDropdownFilter && (
        <FeatureGatedButton
          priority="medium"
          size="small"
          isFeatureLocked
          onClickLocked={showUpgradeModal}
          caret
        >
          State
        </FeatureGatedButton>
      )}
      {showHourStateDropdown && (
        <HourStateDropdown
          open={isHourStateDropdownOpen}
          onOpenChange={setIsHourStateDropdownOpen}
          onChange={handleHourStateFilterDropdownChange}
          selectedStateIds={filters.state_ids}
          multiselect
          filterMode
          showNoState
        >
          <ClearableButton
            priority="medium"
            size="small"
            data-testid="report_batch_update_state"
            caret
            onClear={
              filters.state_ids.length > 0
                ? () => {
                    handleHourStateFilterDropdownChange(null);
                    handleCloseHourStateDropdown();
                  }
                : undefined
            }
            isActive={null}
            selected={filters.state_ids.length > 0}
            clearTooltip={'Clear the state filter'}
          >
            {filters.state_ids.length > 0 ? (
              <>{`${filters.state_ids.length} selected`}</>
            ) : (
              <>
                <Icon name="Contrast01" type="solid" />
                {'State'}
              </>
            )}
          </ClearableButton>
        </HourStateDropdown>
      )}
      {!!(scope & TimesheetScopes.Project) && (
        <TabbedUserFilterDropdown
          unrestrictedWhenEmpty
          onChange={handleUserTeamDropdownFilterChange}
          selectedUsers={filters.user_ids}
          selectedTeams={filters.team_ids}
          users={users}
          teams={teams}
          size="small"
        />
      )}
      {!!(scope & TimesheetScopes.User) && (
        <SearchingProjectFilterDropdown
          unrestrictedWhenEmpty
          selected={filters.project_ids}
          projects={projects}
          numTotalProjects={account.total_projects_count}
          numActiveProjects={account.active_projects_count}
          clients={clients}
          onChange={handleProjectFilterChange}
          onSelectAll={handleProjectFilterDropdownSelectAll}
          enableGroupSelectAll={true}
          size="small"
        />
      )}
      <TagFilterDropdown
        unrestrictedWhenEmpty
        selected={filters.label_ids}
        tags={props.tags}
        tagFilterMode={filters.label_filter}
        onChange={handleTagFilterChange}
        onTagFilterModeChange={handleTagFilterModeChange}
        size="small"
      />
      {showLinkedWorkSyncFilter && (
        <LinkedWorkSyncFilterDropdown
          selectedExternalLinkFilters={filters.external_link_filter?.split(',').filter(Boolean)}
          selectedSyncStatusFilters={filters.sync_status_filter?.split(',').filter(Boolean)}
          onChange={handleLinkedWorkSyncFilterChange}
          size="small"
        />
      )}
      {props.children}
      {hasModifiedFilters && !noClearButton && (
        <ClearFilterButton
          onClick={props.onClearQuery}
          testId="clear-filter-reportqueryfilter"
          size="small"
        />
      )}
    </div>
  );
};

export default ReportQueryFilters;
